// Declaration parsing helpers for normalization warning/semicolon logic.
// Example: startsDeclarationAt("color: red", 0) -> true.
import {
  isIdentifierChar,
  isIdentifierStart,
  isPropertyNameChar,
  isPropertyNameStart,
  skipWhitespace,
} from './chars'

// Check whether declaration-like token starts at given line index.
export function startsDeclarationAt(line: string, startIndex: number): boolean {
  // Return true for custom-property or regular declaration starts.
  return startsCustomPropertyDeclarationAt(line, startIndex) || startsPropertyDeclarationAt(line, startIndex)
}

// Check custom-property declaration starts like `--name:`.
export function startsCustomPropertyDeclarationAt(line: string, startIndex: number): boolean {
  let cursor = startIndex
  // Reject custom-property starts without leading `--`.
  if (line[cursor] !== '-' || line[cursor + 1] !== '-') {
    return false
  } else {
    // Continue custom-property key parsing.
  }

  cursor += 2
  // Reject names with invalid starting character.
  if (!isIdentifierStart(line[cursor] ?? '')) {
    return false
  } else {
    // Continue declaration name scanning.
  }

  cursor += 1
  // Consume custom-property identifier segment.
  while (cursor < line.length && isIdentifierChar(line[cursor])) {
    cursor += 1
  }

  cursor = skipWhitespace(line, cursor)
  // Return true when next token is declaration colon.
  return line[cursor] === ':'
}

// Detect standard property declaration starts like `color:`.
export function startsPropertyDeclarationAt(line: string, startIndex: number): boolean {
  let cursor = startIndex
  const first = line[cursor] ?? ''
  // Reject property names with invalid first character.
  if (!isPropertyNameStart(first)) {
    return false
  } else {
    // Continue property name scanning.
  }

  cursor += 1
  // Consume property-name characters until whitespace/colon.
  while (cursor < line.length && isPropertyNameChar(line[cursor])) {
    cursor += 1
  }

  cursor = skipWhitespace(line, cursor)
  // Return true when next token is declaration colon.
  return line[cursor] === ':'
}

// Find declaration colon index for custom-property declaration lines.
export function findDeclarationColonIndex(trimmed: string): number {
  let cursor = 0
  // Reject lines that do not start with custom-property prefix.
  if (trimmed[cursor] !== '-' || trimmed[cursor + 1] !== '-') {
    return -1
  } else {
    // Continue custom-property declaration scan.
  }

  cursor += 2
  // Reject identifiers with invalid starting character.
  if (!isIdentifierStart(trimmed[cursor] ?? '')) {
    return -1
  } else {
    // Continue declaration name scanning.
  }

  cursor += 1
  // Consume custom-property identifier characters.
  while (cursor < trimmed.length && isIdentifierChar(trimmed[cursor])) {
    cursor += 1
  }

  cursor = skipWhitespace(trimmed, cursor)
  // Return colon index when declaration format is valid.
  return trimmed[cursor] === ':' ? cursor : -1
}
